import type { OnboardingSetupForm } from '@/types/onboarding';

const STORAGE_KEY = 'braise:onboarding-flow';

export interface OnboardingFlowSnapshot {
  tenantId: string;
  tenantSlug: string;
  tenantName: string | null;
  email: string;
  paymentComplete: boolean;
  questionnaire?: OnboardingSetupForm;
}

const isBrowser = typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

export const loadOnboardingSnapshot = (): OnboardingFlowSnapshot | null => {
  if (!isBrowser) {
    return null;
  }
  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as OnboardingFlowSnapshot;
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.tenantId === 'string' &&
      typeof parsed.tenantSlug === 'string' &&
      typeof parsed.email === 'string' &&
      typeof parsed.paymentComplete === 'boolean'
    ) {
      return {
        ...parsed,
        tenantName: parsed.tenantName ?? null,
        questionnaire: parsed.questionnaire ?? undefined,
      };
    }
  } catch {
    window.sessionStorage.removeItem(STORAGE_KEY);
  }
  return null;
};

export const saveOnboardingSnapshot = (snapshot: OnboardingFlowSnapshot): void => {
  if (!isBrowser) {
    return;
  }
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
};

export const updateOnboardingSnapshot = (
  updater: (current: OnboardingFlowSnapshot | null) => OnboardingFlowSnapshot | null,
): void => {
  const next = updater(loadOnboardingSnapshot());
  if (!next) {
    clearOnboardingSnapshot();
    return;
  }
  saveOnboardingSnapshot(next);
};

export const clearOnboardingSnapshot = (): void => {
  if (!isBrowser) {
    return;
  }
  window.sessionStorage.removeItem(STORAGE_KEY);
};

export const onboardingStorageKey = STORAGE_KEY;
